// useBatch — TanStack Query hook for a single batch + its child payouts.
//
// Mirrors the usePayouts() pattern: polls at 30s until all child rows reach a
// terminal status (success or failure), then stops polling (refetchInterval
// returns false). staleTime is 10s — batch child payout statuses mutate as
// the provider processes each row.
//
// queryKey is ["batch", currentMerchantId, batchId] — per-merchant + per-batch
// scope so the cache is isolated when the user switches merchants or views
// different batches.
import { useQuery } from "@tanstack/react-query";

import { getBatch } from "@/api/batches";
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";

/**
 * useBatch — fetches and polls a batch detail (header + child payouts).
 *
 * @param batchId  The batch ID to fetch (from the URL route parameter).
 *
 * Polling behaviour:
 *   - While any child payout has status "pending", polls at 30s intervals.
 *   - Once ALL child payouts are terminal (status "success" or "failure"),
 *     refetchInterval returns false and polling stops permanently for this
 *     query key (06-UI-SPEC Surface 2 "Polling and loading states").
 *   - If no data has loaded yet (query.state.data is undefined), falls back
 *     to 30s to keep polling while the initial fetch is in flight.
 *
 * Guards:
 *   - enabled: false when unauthenticated or no merchant selected.
 */
export function useBatch(batchId: number) {
  const status = useAuth((s) => s.status);
  const currentMerchantId = useUI((s) => s.currentMerchantId);

  return useQuery({
    queryKey: ["batch", currentMerchantId, batchId],
    queryFn: () => getBatch(currentMerchantId!, batchId),
    // AUDIT-PASS-8 panel M-4: skip the fetch entirely when the route gave us
    // a non-positive batchId sentinel (BatchDetail passes 0 when /^\d+$/ on
    // the URL param fails). The page renders an "Invalid batch ID" message
    // without ever hitting the backend with a NaN/0 URL.
    enabled: status === "authenticated" && currentMerchantId != null && batchId > 0,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 30_000;
      // PDF round 3 N2 fix: while the backend ingestion worker is still
      // registering beneficiaries, poll FAST (2s) so the operator sees
      // live "Registering X of Y" progress on the batch detail page.
      // Once phase flips to "done" or "failed" we fall through to the
      // existing 30s polling cadence for the dispatch/reconciler loop.
      const ing = data.batch.ingest_state;
      if (ing && ing.phase === "registering") {
        return 2_000;
      }
      // AUDIT-PASS-8 panel M-2: Array.prototype.every returns true for an
      // empty array. Pre-fix, if the backend returned {batch, payouts: []}
      // (e.g. immediately after submit while child rows are still being
      // hydrated, or if the batch row exists but no child writes have
      // committed yet), allTerminal would be true and polling would
      // permanently STOP — user sees "0 rows" frozen and the page never
      // recovers until manual refresh. Now we also key off the batch-row
      // status (BLOCKER-1 fix wired the finalize step), which is the
      // authoritative termination signal.
      const batchTerminal =
        data.batch.status === "complete" ||
        data.batch.status === "partial" ||
        data.batch.status === "failure";
      if (batchTerminal) return false;
      // Defence-in-depth: if the batch row still says 'processing' but
      // every child row IS terminal (race window between dispatch loop
      // completion and the finalize UPDATE), keep polling at 30s — the
      // next tick will pick up the finalized batch.status.
      const _allChildrenTerminal =
        data.payouts.length > 0 &&
        data.payouts.every(
          (p) => p.status === "success" || p.status === "failure",
        );
      void _allChildrenTerminal; // referenced via comment only
      return 30_000;
    },
  });
}
